// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import ParentAction from '../Parent/ParentAction';
import StudentNameOrderToggle from '../../components/StudentNameOrderToggle';
import { fetchTeacherSubjectsWithStudents } from '../../services/api';
import { sortStudentsByName, useStudentNameOrder } from '../../utils/studentSort';

const ALL = 'all';
const SUBJECTS = ['Literacy', 'Math', 'Science', 'Social Studies', 'Success Skills'];
const RETURN_TO = { pathname: '/teacher/guided-actions', label: 'Back to Guided Actions' };

const panelStyle = { background: '#fff', border: '1px solid #dbe4f0', borderRadius: 18, padding: 18, boxShadow: 'var(--ae-shadow-card)' };
const labelStyle = { display: 'grid', gap: 6, fontSize: 12, fontWeight: 800, color: '#1e3a5f' };
const selectStyle = { minWidth: 0, padding: '9px 12px', border: '1px solid var(--ae-border)', borderRadius: 8, fontSize: 14, background: '#fff' };

export function groupTeacherClasses(data) {
  const classes = new Map();
  (Array.isArray(data) ? data : data?.subjects || []).forEach((subject) => {
    (subject.classes || []).forEach((cls) => {
      const id = String(cls.class_id);
      const existing = classes.get(id);
      const students = new Map((existing?.students || []).map((student) => [String(student.student_id), student]));
      (cls.students || []).forEach((student) => students.set(String(student.student_id), student));
      classes.set(id, { ...cls, class_id: id, students: [...students.values()] });
    });
  });
  return [...classes.values()].sort((a, b) => String(a.class_name || '').localeCompare(String(b.class_name || ''), undefined, { numeric: true }));
}

const studentName = (student) => `${student.first_name || ''} ${student.last_name || ''}`.trim() || `Student ${student.student_id}`;

export default function TeacherGuidedActions() {
  const [classes, setClasses] = useState<any>([]);
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>('');
  const [classId, setClassId] = useState<any>('');
  const [studentId, setStudentId] = useState<any>('');
  const [subject, setSubject] = useState<any>('');
  const [nameOrder] = useStudentNameOrder();

  useEffect(() => {
    let cancelled = false;
    fetchTeacherSubjectsWithStudents()
      .then((data) => {
        if (cancelled) return;
        const grouped = groupTeacherClasses(data);
        setClasses(grouped);
        setClassId(grouped[0]?.class_id || '');
      })
      .catch(() => {
        if (!cancelled) setError('Could not load your classes. Please reload to try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const classStudents = useMemo(() => {
    if (classId === ALL) {
      const unique = new Map();
      classes.forEach((cls) => cls.students.forEach((student) => unique.set(String(student.student_id), student)));
      return sortStudentsByName([...unique.values()], nameOrder);
    }
    const cls = classes.find((item) => item.class_id === classId);
    return sortStudentsByName(cls?.students || [], nameOrder);
  }, [classes, classId, nameOrder]);

  // Default to the first student so a large class doesn't load every profile at once.
  useEffect(() => {
    if (studentId === ALL) return;
    if (!classStudents.some((student) => String(student.student_id) === studentId)) {
      setStudentId(classStudents[0] ? String(classStudents[0].student_id) : '');
    }
  }, [classStudents, studentId]);

  const visibleStudents = useMemo(() => (
    studentId === ALL ? classStudents : classStudents.filter((student) => String(student.student_id) === studentId)
  ), [classStudents, studentId]);

  return (
    <div style={{ padding: 6, display: 'grid', gap: 18 }}>
      <section aria-label="Guided Actions filters" style={panelStyle}>
        <h1 style={{ margin: '0 0 8px', fontSize: 28, fontWeight: 800, color: '#1e3a5f' }}>Guided Actions</h1>
        <p style={{ margin: '0 0 16px', fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>
          See the Guided Actions families see for students in your classes. Choose a class, student, and subject to filter.
        </p>
        {loading ? (
          <div role="status" style={{ color: '#64748b', fontSize: 13 }}>Loading your classes…</div>
        ) : error ? (
          <div role="alert" style={{ color: '#991b1b', fontSize: 13 }}>{error}</div>
        ) : classes.length === 0 ? (
          <div style={{ color: '#64748b', fontSize: 13 }}>No classes with students are assigned to you yet.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, alignItems: 'end' }}>
            <label style={labelStyle}>
              Class
              <select value={classId} onChange={(event) => setClassId(event.target.value)} style={selectStyle}>
                <option value={ALL}>All classes</option>
                {classes.map((cls) => (
                  <option key={cls.class_id} value={cls.class_id}>{cls.class_name || `Class ${cls.class_id}`}</option>
                ))}
              </select>
            </label>
            <label style={labelStyle}>
              Student
              <select value={studentId} onChange={(event) => setStudentId(event.target.value)} style={selectStyle} disabled={classStudents.length === 0}>
                {classStudents.length > 0 && <option value={ALL}>All students ({classStudents.length})</option>}
                {classStudents.map((student) => (
                  <option key={student.student_id} value={String(student.student_id)}>{studentName(student)}</option>
                ))}
              </select>
            </label>
            <label style={labelStyle}>
              Subject
              <select value={subject} onChange={(event) => setSubject(event.target.value)} style={selectStyle}>
                <option value="">All subjects</option>
                {SUBJECTS.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </label>
            <StudentNameOrderToggle />
          </div>
        )}
      </section>

      {!loading && !error && classes.length > 0 && (
        visibleStudents.length === 0 ? (
          <div style={{ ...panelStyle, color: '#64748b', fontSize: 13 }}>No students in this class yet.</div>
        ) : (
          <ParentAction
            studentsOverride={visibleStudents}
            basePath="/teacher"
            returnTo={RETURN_TO}
            subjectFilter={subject}
            showOverview={false}
          />
        )
      )}
    </div>
  );
}
